import React, { useRef, useCallback, useState, useEffect } from 'react';
import { EmptyState } from '../../../components/common/EmptyState';
import { PeriodSelector } from '../../../components/common/PeriodSelector';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { TabsContent } from '../../../components/ui/tabs';
import api, { resultadosApi, expensasApi } from '../../../lib/api';
import { cn, formatImporte } from '../../../lib/utils';
import { useViewportFillHeight } from '../../../hooks/useViewportFillHeight';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Home, RefreshCw, FileText, AlertTriangle } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';
import { CargaFacturasServicios } from '../../../components/analista/CargaFacturasServicios';
import { GastosNegocioDialog } from '../../../components/analista/GastosNegocioDialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../../../components/ui/alert-dialog';

// Input que muestra formato $ con puntos/comas cuando no está en edición
const FacturasPdf = ({ facturas }) => {
  if (!facturas?.length) return null;
  const abrir = async (f) => {
    try {
      const r = await api.get('/egresos/factura-servicio', { params: { path: f.storage_path }, responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch {
      sonnerToast.error('No se pudo abrir la factura');
    }
  };
  return (
    <span className="flex items-center shrink-0">
      {facturas.map((f, i) => (
        <button key={i} type="button" onClick={() => abrir(f)} title={`${f.archivo}${f.nic ? ` · NIC ${f.nic}` : ''}`}
          className="p-0.5 text-red-600 hover:text-red-800" data-testid="egreso-factura-pdf">
          <FileText className="w-3.5 h-3.5" />
        </button>
      ))}
    </span>
  );
};

const AvisoManual = ({ calculado, formatImporte }) => (
  <span title={`Modificado a mano · según la liquidación de gastos: $ ${formatImporte(calculado, 0)}`} className="shrink-0 text-amber-600" data-testid="negocio-aviso-manual">
    <AlertTriangle className="w-3.5 h-3.5" />
  </span>
);

const EgresoInput = ({ value, onChange, formatImporte, facturas, calculado }) => {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const numVal = parseFloat(value) || 0;
  const hasValue = value !== '' && value !== undefined && value !== null && numVal > 0;
  const manual = calculado != null && Math.abs(numVal - calculado) > 0.5;

  const handleBlur = () => {
    setTimeout(() => {
      if (!document.hasFocus()) return;
      setFocused(false);
    }, 150);
  };

  if (!focused && hasValue) {
    return (
      <div className="flex items-center justify-end gap-0.5">
        <FacturasPdf facturas={facturas} />
        {manual && <AvisoManual calculado={calculado} formatImporte={formatImporte} />}
        <div
          className="h-8 flex items-center justify-end px-2 text-sm font-mono rounded border border-transparent hover:border-slate-300 cursor-text text-slate-900 whitespace-nowrap"
          onClick={() => { setFocused(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        >
          $ {formatImporte(numVal, 0)}
        </div>
      </div>
    );
  }

  if (manual) {
    return (
      <div className="flex items-center gap-0.5">
        <AvisoManual calculado={calculado} formatImporte={formatImporte} />
        <Input ref={inputRef} type="number" min="0" step="0.01" value={value} onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)} onBlur={handleBlur} className="h-8 text-right text-sm font-mono w-full" placeholder="0" />
      </div>
    );
  }

  return (
    <Input
      ref={inputRef}
      type="number"
      min="0"
      step="0.01"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      className="h-8 text-right text-sm font-mono w-full"
      placeholder="0"
    />
  );
};

export const TabEgresos = ({ cn, contratos, contratoBadgeByUnidad = {}, edificio, edificioId, egresosData, egresosHasChanges, error, expandedGrupos, facturacionData, formatImporte, getGroupedOrder, navigatePeriodo, saving, savingEgresos, selectedPeriodo, setEgresosData, setEgresosHasChanges, setSavingEgresos, setSelectedPeriodo, sortUnidades, toast, toggleGrupoExpanded, unidades }) => {
  const [tableRef, tableHeight] = useViewportFillHeight();
  const debounceTimers = useRef({});
  const [expensasAuto, setExpensasAuto] = useState({}); // {unidad_id: total}
  const [expensasEstado, setExpensasEstado] = useState(null); // 'aprobado'|'borrador'|null
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Auto-cargar expensas calculadas desde el módulo Expensas (Administrador de Consorcio)
  useEffect(() => {
    if (!edificioId || !selectedPeriodo) {
      setExpensasAuto({}); setExpensasEstado(null); return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await expensasApi.getExpensasResumenPorMes(edificioId, selectedPeriodo);
        if (cancelled) return;
        if (r.data?.existe) {
          setExpensasAuto(r.data.expensas_para_liquidacion || r.data.liquidaciones_por_unidad || {});
          setExpensasEstado(r.data.estado || null);
        } else {
          setExpensasAuto({});
          setExpensasEstado(null);
        }
      } catch {
        if (!cancelled) { setExpensasAuto({}); setExpensasEstado(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [edificioId, selectedPeriodo]);

  // Estado para expandir/colapsar el bloque Piso 1 en Torre Emilia.
  const [piso1Expandido, setPiso1Expandido] = useState(false);
  const PISO1_ID = 'unit_piso1_te';
  const SUBSUELO_ID = 'unit_subsuelo_te';

  const saveFieldForUnit = useCallback(async (unidadId, periodo, allDataForUnit) => {
    try {
      const dataToSave = { ...allDataForUnit };
      await resultadosApi.saveEgresos(edificioId, unidadId, periodo, dataToSave);
    } catch (err) {
      console.error('Error auto-saving egreso:', err);
      toast.error('Error al guardar egreso');
    }
  }, [edificioId, toast]);

  // Reload egresos del período (tras sincronizar expensas)
  const reloadEgresos = useCallback(async () => {
    try {
      const res = await resultadosApi.getEgresos(edificioId, selectedPeriodo);
      setEgresosData(res.data || {});
      setEgresosHasChanges(false);
    } catch (err) {
      console.error('Error recargando egresos:', err);
    }
  }, [edificioId, selectedPeriodo, setEgresosData, setEgresosHasChanges]);

  const doSyncExpensas = useCallback(async () => {
    setSyncing(true);
    try {
      const r = await resultadosApi.syncExpensasEgresos(edificioId, selectedPeriodo);
      await reloadEgresos();
      toast.success(`Expensas actualizadas desde la liquidación aprobada (${r.data?.actualizadas ?? 0} unidades)`);
    } catch (err) {
      console.error('Error sincronizando expensas:', err);
      toast.error('Error al actualizar expensas');
    } finally {
      setSyncing(false);
      setSyncConfirmOpen(false);
    }
  }, [edificioId, selectedPeriodo, reloadEgresos, toast]);

  const handleClickSyncExpensas = useCallback(() => {
    // ¿Hay valores manuales en el período visible? → pedir confirmación.
    const hayManuales = Object.entries(egresosData).some(
      ([k, v]) => k.endsWith(`-${selectedPeriodo}`) && v?.expensas_manual
    );
    if (hayManuales) {
      setSyncConfirmOpen(true);
    } else {
      doSyncExpensas();
    }
  }, [egresosData, selectedPeriodo, doSyncExpensas]);

  return (
        <>
        <TabsContent value="egresos" className="mt-0">
          {(() => {
            const esTorreEmilia = edificio?.modelo_administracion === 'gc_factura_y_cobra';
            // ── Cálculo del universo de unidades a mostrar ──
            // Torre Emilia: incluye unidades administradas + miembros del Piso 1 (heredan admin del padre sintético).
            // Excluye componentes (cocheras de oficinas) cuando NO sean del Piso 1.
            let unidadesBase;
            if (esTorreEmilia) {
              // Set de unidad_ids del Piso 1 (todos los miembros del conjunto).
              const piso1MiembrosIds = new Set(
                unidades.filter(u => u.conjunto_alquilable_id === 'conj_piso1_te' && u.unidad_id !== PISO1_ID).map(u => u.unidad_id)
              );
              unidadesBase = unidades.filter(u => {
                // Incluir si es la unidad sintética Piso 1
                if (u.unidad_id === PISO1_ID) return true;
                // Incluir si está administrada y NO es componente (ej. cochera de O700)
                if (u.en_administracion && u.rol_unidad !== 'componente') {
                  // Excluir miembros del Piso 1 del listado plano (aparecen al expandir).
                  if (piso1MiembrosIds.has(u.unidad_id)) return false;
                  return true;
                }
                return false;
              });
            } else {
              unidadesBase = unidades.filter(u => u.en_administracion);
            }
            const unidadesAdmin = sortUnidades(unidadesBase);
            // Miembros internos del Piso 1 (incluso si no tienen en_administracion=true, heredan del padre).
            const piso1Miembros = sortUnidades(
              unidades.filter(u => u.conjunto_alquilable_id === 'conj_piso1_te' && u.unidad_id !== PISO1_ID)
            );
            const tiposEgreso = [
              { key: 'retenciones', label: 'Retenciones' },
              { key: 'iibb', label: 'IIBB (4%)' },
              { key: 'edemsa', label: 'Edemsa' },
              { key: 'tasa_municipal', label: 'Tasa Municipal' },
              { key: 'agua', label: 'Agua' },
              { key: 'expensas', label: 'Expensas' },
              { key: 'gastos_negocio', label: 'Negocio' },
            ];

            const getIngresoUnidad = (unidadId) => {
              if (!facturacionData?.items) return 0;
              const item = facturacionData.items.find(i => i.unidad_id === unidadId);
              return item?.monto_neto || 0;
            };

            const calcularIIBB = (unidadId) => {
              const ingreso = getIngresoUnidad(unidadId);
              return Math.round(ingreso * 0.04 * 100) / 100;
            };

            // Calcula retención automática a partir del contrato vigente de la unidad.
            const calcularRetencionAuto = (unidadId) => {
              const contratoVigente = (contratos || []).find(c => {
                if (c.estado !== 'vigente') return false;
                if (c.unidad_id === unidadId) return true;
                return (c.unidades_contrato || []).some(uc => uc.unidad_id === unidadId);
              });
              if (!contratoVigente) return 0;
              const tipo = contratoVigente.retencion_tipo || 'sin_retenciones';
              let pct = 0;
              if (tipo === '2') pct = 2;
              else if (tipo === '4') pct = 4;
              else if (tipo === '6') pct = 6;
              else if (tipo === 'otro') pct = parseFloat(contratoVigente.retencion_porcentaje) || 0;
              if (pct <= 0) return 0;
              const ingreso = getIngresoUnidad(unidadId);
              return Math.round(ingreso * pct) / 100;
            };

            const handleEgresoChange = (unidadId, tipo, valor) => {
              const key = `${unidadId}-${selectedPeriodo}`;
              
              setEgresosData(prev => {
                const updated = {
                  ...prev,
                  [key]: {
                    ...(prev[key] || {}),
                    [tipo]: valor,
                    // Marcar expensas como manual cuando el analista la edita.
                    ...(tipo === 'expensas' ? { expensas_manual: true } : {}),
                    // IIBB: manual SOLO si se edita la celda de IIBB (vacío = volver a auto).
                    ...(tipo === 'iibb' ? { iibb_manual: valor !== '' && valor !== undefined && valor !== null } : {}),
                    // Retenciones: manual SOLO si se edita su celda (vacío = volver al % del contrato).
                    ...(tipo === 'retenciones' ? { retenciones_manual: valor !== '' && valor !== undefined && valor !== null } : {})
                  }
                };

                const timerKey = `${key}-${tipo}`;
                if (debounceTimers.current[timerKey]) {
                  clearTimeout(debounceTimers.current[timerKey]);
                }
                debounceTimers.current[timerKey] = setTimeout(() => {
                  const dataForUnit = updated[key] || {};
                  // Si el IIBB no fue editado a mano, se guarda el auto-calculado (4% del
                  // neto vigente en 5.Factura) pero SIN marca manual: así sigue recalculándose.
                  const iibbManualFlag = !!dataForUnit.iibb_manual;
                  const toSave = {
                    ...dataForUnit,
                    iibb: iibbManualFlag && dataForUnit.iibb !== undefined && dataForUnit.iibb !== '' && dataForUnit.iibb !== null
                      ? dataForUnit.iibb
                      : calcularIIBB(unidadId),
                    iibb_manual: iibbManualFlag,
                    // Retenciones automáticas (% del contrato) se guardan siempre, sin marca manual.
                    retenciones: dataForUnit.retenciones_manual && dataForUnit.retenciones !== undefined && dataForUnit.retenciones !== '' && dataForUnit.retenciones !== null
                      ? dataForUnit.retenciones
                      : calcularRetencionAuto(unidadId),
                    retenciones_manual: !!dataForUnit.retenciones_manual
                  };
                  saveFieldForUnit(unidadId, selectedPeriodo, toSave);
                  delete debounceTimers.current[timerKey];
                }, 600);

                return updated;
              });
            };

            const getEgresoValue = (unidadId, tipo) => {
              const key = `${unidadId}-${selectedPeriodo}`;
              const manual = egresosData[key]?.[tipo];
              if (tipo === 'retenciones') {
                const esManualRet = egresosData[key]?.retenciones_manual;
                if (esManualRet && manual !== undefined && manual !== '' && manual !== null) return manual;
                const auto = calcularRetencionAuto(unidadId);
                return auto > 0 ? auto : '';
              }
              if (tipo === 'iibb') {
                // El valor guardado solo manda si el analista editó IIBB a mano;
                // si no, siempre 4% del neto vigente en 5. Factura (incluye monto manual).
                const esManualIibb = egresosData[key]?.iibb_manual;
                if (esManualIibb && manual !== undefined && manual !== '' && manual !== null) return manual;
                return calcularIIBB(unidadId);
              }
              if (tipo === 'expensas') {
                // Editable manualmente. Si el analista cargó un valor manual, se muestra ese;
                // si no, se toma de la liquidación aprobada (cuando exista).
                const manualFlag = egresosData[key]?.expensas_manual;
                const manualVal = egresosData[key]?.expensas;
                if (manualFlag) {
                  return (manualVal !== undefined && manualVal !== null) ? manualVal : '';
                }
                if (expensasEstado !== 'aprobado') return '';
                const auto = expensasAuto[unidadId];
                const absAuto = Math.abs(parseFloat(auto) || 0);
                return absAuto > 0 ? absAuto : '';
              }
              return manual || '';
            };

            // Suma de un campo para los miembros del Piso 1 (Torre Emilia).
            // Expensas: Piso 1 se rinde como UNA sola unidad al propietario del piso.
            // Se toma la liquidación de la unidad principal (unit_piso1_te) MÁS las
            // liquidaciones separadas de sus cocheras asociadas (ej. C007/C008), que se
            // liquidan por cochera y no están incluidas en la liquidación principal.
            // Las unidades internas de alquiler (O101.., W0..) no tienen liquidación propia
            // de expensas, por lo que suman 0 y no distorsionan el total.
            const sumMiembrosPiso1 = (tipo) => {
              if (tipo === 'expensas') {
                if (expensasEstado !== 'aprobado') return 0;
                return [PISO1_ID, ...piso1Miembros.map(m => m.unidad_id)]
                  .reduce((s, id) => s + Math.abs(parseFloat(expensasAuto[id] || 0) || 0), 0);
              }
              return piso1Miembros.reduce((s, m) => s + (parseFloat(getEgresoValue(m.unidad_id, tipo)) || 0), 0);
            };

            const calcularTotalUnidad = (unidadId) => {
              // Torre Emilia: el bloque Piso 1 agrega valores mixtos (miembros + bloque).
              if (esTorreEmilia && unidadId === PISO1_ID) {
                return ['retenciones', 'iibb', 'expensas'].reduce((s, t) => s + sumMiembrosPiso1(t), 0)
                  + (parseFloat(getEgresoValue(PISO1_ID, 'edemsa')) || 0)
                  + (parseFloat(getEgresoValue(PISO1_ID, 'tasa_municipal')) || 0)
                  + (parseFloat(getEgresoValue(PISO1_ID, 'agua')) || 0)
                  + (parseFloat(getEgresoValue(PISO1_ID, 'gastos_negocio')) || 0);
              }
              const key = `${unidadId}-${selectedPeriodo}`;
              const data = egresosData[key] || {};
              return tiposEgreso.reduce((sum, t) => {
                if (t.key === 'retenciones') {
                  const val = parseFloat(getEgresoValue(unidadId, 'retenciones')) || 0;
                  return sum + (val || 0);
                }
                if (t.key === 'iibb') {
                  const manual = data.iibb_manual ? data['iibb'] : undefined;
                  const val = (manual !== undefined && manual !== '' && manual !== null) ? parseFloat(manual) : calcularIIBB(unidadId);
                  return sum + (val || 0);
                }
                if (t.key === 'expensas') {
                  // Manual o auto (liquidación aprobada). getEgresoValue resuelve la prioridad.
                  return sum + (parseFloat(getEgresoValue(unidadId, 'expensas')) || 0);
                }
                return sum + (parseFloat(data[t.key]) || 0);
              }, 0);
            };

            const calcularTotalColumna = (tipo) => {
              return unidadesAdmin.reduce((sum, u) => {
                if (esTorreEmilia && u.unidad_id === PISO1_ID) {
                  // Para el bloque Piso 1: Retenciones/IIBB/Expensas vienen de los miembros.
                  if (tipo === 'retenciones' || tipo === 'iibb' || tipo === 'expensas') {
                    return sum + sumMiembrosPiso1(tipo);
                  }
                  // Edemsa, Tasa, Agua, Negocio se cargan a nivel bloque.
                  return sum + (parseFloat(getEgresoValue(PISO1_ID, tipo)) || 0);
                }
                return sum + (parseFloat(getEgresoValue(u.unidad_id, tipo)) || 0);
              }, 0);
            };

            const calcularTotalGeneral = () => {
              return unidadesAdmin.reduce((sum, u) => sum + calcularTotalUnidad(u.unidad_id), 0);
            };

            if (unidadesAdmin.length === 0) {
              return (
                <EmptyState
                  icon={Home}
                  title="Sin unidades en administración"
                  description="No hay unidades en administración para registrar egresos."
                />
              );
            }

            return (
              <div>
                {/* Controles */}
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriodo('prev')}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <div className="w-[160px]">
                      <PeriodSelector
                        value={selectedPeriodo}
                        onChange={setSelectedPeriodo}
                        placeholder="Período"
                      />
                    </div>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriodo('next')}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Botón: re-sincronizar expensas desde la liquidación aprobada */}
                  <div className="flex items-center gap-2">
                    <CargaFacturasServicios edificioId={edificioId} periodo={selectedPeriodo} onDone={reloadEgresos} />
                    <GastosNegocioDialog edificioId={edificioId} periodo={selectedPeriodo} onDone={reloadEgresos} />
                    {expensasEstado && expensasEstado !== 'aprobado' && (
                      <span className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        Liquidación de Expensas pendiente de aprobación
                      </span>
                    )}
                    {!expensasEstado && (
                      <span className="text-[11px] font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                        Sin período de Expensas creado
                      </span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1.5"
                      onClick={handleClickSyncExpensas}
                      disabled={syncing || expensasEstado !== 'aprobado'}
                      title={expensasEstado === 'aprobado'
                        ? 'Re-sincroniza la columna Expensas desde la liquidación aprobada'
                        : 'No hay liquidación de Expensas aprobada para sincronizar'}
                      data-testid="actualizar-expensas-btn"
                    >
                      <RefreshCw className={cn('w-3.5 h-3.5', syncing && 'animate-spin')} />
                      Actualizar expensas
                    </Button>
                  </div>
                </div>

                {/* Tabla de egresos */}
                <div ref={tableRef} className="bg-white border border-slate-200 rounded-lg overflow-auto" style={{ height: tableHeight }}>
                  <table className="w-full text-sm min-h-full [&_tfoot_td]:sticky [&_tfoot_td]:bottom-0 [&_tfoot_td]:z-10 [&_tfoot_td]:bg-slate-100">
                    <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-20 shadow-[0_1px_0_0_rgb(226,232,240)]">
                      <tr>
                        <th className="py-2.5 px-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider bg-slate-50">Unidad</th>
                        {tiposEgreso.map(tipo => (
                          <th key={tipo.key} className="py-2.5 px-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider min-w-[140px] bg-slate-50">
                            {tipo.label}
                          </th>
                        ))}
                        <th className="py-2.5 px-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider min-w-[130px] bg-slate-50">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {/* ─── Render Torre Emilia (lógica de bloque Piso 1) ─── */}
                      {esTorreEmilia ? unidadesAdmin.map(unidad => {
                        // Render especial: bloque Piso 1
                        if (unidad.unidad_id === PISO1_ID) {
                          // Cálculo de valores agregados.
                          // - Retenciones/IIBB: suma de miembros.
                          // - Resto: valor único bajo unit_piso1_te.
                          const sumRet = piso1Miembros.reduce((s, m) => s + (parseFloat(getEgresoValue(m.unidad_id, 'retenciones')) || 0), 0);
                          const sumIIBB = piso1Miembros.reduce((s, m) => s + (parseFloat(getEgresoValue(m.unidad_id, 'iibb')) || 0), 0);
                          // Expensas del bloque Piso 1: se consolida la liquidación de la
                          // unidad principal (unit_piso1_te) + las cocheras asociadas del piso.
                          const sumExpensasMiembros = sumMiembrosPiso1('expensas');
                          const valBloque = (tipo) => parseFloat(getEgresoValue(PISO1_ID, tipo)) || 0;
                          const totalBloque = sumRet + sumIIBB + valBloque('edemsa') + valBloque('tasa_municipal') + valBloque('agua') + sumExpensasMiembros + valBloque('gastos_negocio');
                          const filas = [
                            <tr
                              key={`piso1-bloque`}
                              className="bg-indigo-50 hover:bg-indigo-100/60 font-semibold cursor-pointer"
                              onClick={() => setPiso1Expandido(v => !v)}
                              data-testid="egresos-row-piso1"
                            >
                              <td className="py-2.5 px-3 text-indigo-900">
                                <span className="inline-flex items-center gap-1.5">
                                  {piso1Expandido ? <ChevronUp className="w-4 h-4 text-indigo-400" /> : <ChevronDown className="w-4 h-4 text-indigo-400" />}
                                  Piso 1
                                </span>
                              </td>
                              {/* Retenciones: suma read-only */}
                              <td className="py-2.5 px-3 text-right font-mono text-sm text-indigo-900 whitespace-nowrap">$ {formatImporte(sumRet, 0)}</td>
                              {/* IIBB: suma read-only */}
                              <td className="py-2.5 px-3 text-right font-mono text-sm text-indigo-900 whitespace-nowrap">$ {formatImporte(sumIIBB, 0)}</td>
                              {/* Edemsa, Tasa, Agua, Expensas, Negocio: editables solo cuando colapsado */}
                              {['edemsa', 'tasa_municipal', 'agua', 'expensas', 'gastos_negocio'].map(tipoKey => (
                                <td key={tipoKey} className="py-1 px-2" onClick={(e) => e.stopPropagation()}>
                                  {tipoKey === 'expensas' ? (
                                    (() => {
                                      if (expensasEstado === 'aprobado' && sumExpensasMiembros > 0) {
                                        return <div title="Expensas de Piso 1: liquidación de la unidad principal + cocheras asociadas (tomada de la liquidación aprobada — no editable)" className="h-8 flex items-center justify-end px-2 text-sm font-mono rounded bg-emerald-50/60 border border-emerald-200 text-emerald-800 whitespace-nowrap">$ {formatImporte(sumExpensasMiembros, 0)}</div>;
                                      }
                                      return <div className="h-8 flex items-center justify-end px-2 text-sm font-mono rounded bg-slate-50 border border-slate-200 text-slate-400">—</div>;
                                    })()
                                  ) : (
                                    <EgresoInput
                                      value={getEgresoValue(PISO1_ID, tipoKey)}
                                      facturas={egresosData[`${PISO1_ID}-${selectedPeriodo}`]?.[`${tipoKey}_facturas`]}
                                      calculado={tipoKey === 'gastos_negocio' ? egresosData[`${PISO1_ID}-${selectedPeriodo}`]?.gastos_negocio_calculado : undefined}
                                      onChange={(val) => handleEgresoChange(PISO1_ID, tipoKey, val)}
                                      formatImporte={formatImporte}
                                    />
                                  )}
                                </td>
                              ))}
                              <td className="py-2.5 px-3 text-right font-mono text-sm text-green-700 whitespace-nowrap">$ {formatImporte(totalBloque, 0)}</td>
                            </tr>
                          ];
                          if (piso1Expandido) {
                            piso1Miembros.forEach(m => {
                              filas.push(
                                <tr key={`piso1-m-${m.unidad_id}`} className="bg-indigo-50/30 hover:bg-indigo-50/60" data-testid={`egresos-row-piso1-miembro-${m.unidad_id}`}>
                                  <td className="py-2 px-3 font-medium text-indigo-800 border-l-4 border-l-indigo-400">
                                    <span className="inline-block w-4"></span>
                                    <span>{m.nombre_unidad}</span>
                                  </td>
                                  {/* Solo Retenciones e IIBB editables; el resto deshabilitado */}
                                  {tiposEgreso.map(tipo => {
                                    if (tipo.key === 'retenciones' || tipo.key === 'iibb') {
                                      return (
                                        <td key={tipo.key} className="py-1 px-2">
                                          <EgresoInput
                                            value={getEgresoValue(m.unidad_id, tipo.key)}
                                            onChange={(val) => handleEgresoChange(m.unidad_id, tipo.key, val)}
                                            formatImporte={formatImporte}
                                          />
                                        </td>
                                      );
                                    }
                                    return (
                                      <td key={tipo.key} className="py-1 px-2">
                                        <div title="Este concepto se carga en la fila del Piso 1" className="h-8 flex items-center justify-end px-2 text-sm font-mono rounded bg-slate-100 border border-slate-200 text-slate-400">—</div>
                                      </td>
                                    );
                                  })}
                                  <td className="py-2 px-3 text-right font-mono text-sm font-semibold text-green-700 whitespace-nowrap">
                                    $ {formatImporte((parseFloat(getEgresoValue(m.unidad_id, 'retenciones')) || 0) + (parseFloat(getEgresoValue(m.unidad_id, 'iibb')) || 0), 0)}
                                  </td>
                                </tr>
                              );
                            });
                          }
                          return filas;
                        }
                        // Render normal de unidad simple (sin desplegable)
                        return (
                          <tr key={unidad.unidad_id} className="hover:bg-slate-100/50">
                            <td className="py-2 px-3 font-medium text-slate-900 min-w-[200px]">
                              <div>{unidad.nombre_unidad}</div>
                              {contratoBadgeByUnidad[unidad.unidad_id] && !contratoBadgeByUnidad[unidad.unidad_id].es_principal && (
                                <span
                                  className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] ring-1 whitespace-nowrap bg-violet-50 text-violet-700 ring-violet-200"
                                  title={contratoBadgeByUnidad[unidad.unidad_id].inquilino}
                                  data-testid={`badge-contrato-egr-${unidad.unidad_id}`}
                                >
                                  {contratoBadgeByUnidad[unidad.unidad_id].inquilino}
                                </span>
                              )}
                            </td>
                            {tiposEgreso.map(tipo => (
                              <td key={tipo.key} className="py-1 px-2">
                                {tipo.key === 'expensas' ? (
                                  <div data-testid={`expensas-input-${unidad.unidad_id}`} className={cn(
                                    'rounded',
                                    egresosData[`${unidad.unidad_id}-${selectedPeriodo}`]?.expensas_manual && 'ring-1 ring-amber-300'
                                  )}>
                                    <EgresoInput
                                      value={getEgresoValue(unidad.unidad_id, 'expensas')}
                                      onChange={(val) => handleEgresoChange(unidad.unidad_id, 'expensas', val)}
                                      formatImporte={formatImporte}
                                    />
                                  </div>
                                ) : (
                                  <EgresoInput
                                    value={getEgresoValue(unidad.unidad_id, tipo.key)}
                                    facturas={egresosData[`${unidad.unidad_id}-${selectedPeriodo}`]?.[`${tipo.key}_facturas`]}
                                  calculado={tipo.key === 'gastos_negocio' ? egresosData[`${unidad.unidad_id}-${selectedPeriodo}`]?.gastos_negocio_calculado : undefined}
                                    calculado={tipo.key === 'gastos_negocio' ? egresosData[`${unidad.unidad_id}-${selectedPeriodo}`]?.gastos_negocio_calculado : undefined}
                                    onChange={(val) => handleEgresoChange(unidad.unidad_id, tipo.key, val)}
                                    formatImporte={formatImporte}
                                  />
                                )}
                              </td>
                            ))}
                            <td className="py-2 px-3 text-right font-mono text-sm font-semibold whitespace-nowrap">
                              <span className="text-green-700">$ {formatImporte(calcularTotalUnidad(unidad.unidad_id), 0)}</span>
                            </td>
                          </tr>
                        );
                      }) : getGroupedOrder(unidadesAdmin).map((entry) => {
                        if (entry.type === 'group_summary') {
                          const uids = entry.unidades;
                          const isExpanded = expandedGrupos[entry.grupo.grupo_id];
                          return (
                            <tr key={`egs-${entry.grupo.grupo_id}`} className="bg-indigo-50 hover:bg-indigo-100/60 font-semibold cursor-pointer" onClick={() => toggleGrupoExpanded(entry.grupo.grupo_id)}>
                              <td className="py-2.5 px-3 text-indigo-900">
                                <span className="inline-flex items-center gap-1.5">
                                  {isExpanded ? <ChevronUp className="w-4 h-4 text-indigo-400" /> : <ChevronDown className="w-4 h-4 text-indigo-400" />}
                                  {entry.grupo.nombre}
                                </span>
                              </td>
                              {tiposEgreso.map(tipo => {
                                const sum = uids.reduce((s, u) => {
                                  if (tipo.key === 'iibb') return s + calcularIIBB(u.unidad_id);
                                  if (tipo.key === 'expensas') {
                                    return s + (parseFloat(getEgresoValue(u.unidad_id, 'expensas')) || 0);
                                  }
                                  const key = `${u.unidad_id}-${selectedPeriodo}`;
                                  return s + (parseFloat(egresosData[key]?.[tipo.key]) || 0);
                                }, 0);
                                return (
                                  <td key={tipo.key} className="py-2.5 px-3 text-right font-mono text-sm text-indigo-900 whitespace-nowrap">
                                    $ {formatImporte(sum, 0)}
                                  </td>
                                );
                              })}
                              <td className="py-2.5 px-3 text-right font-mono text-sm text-green-700 whitespace-nowrap">
                                $ {formatImporte(uids.reduce((s, u) => s + calcularTotalUnidad(u.unidad_id), 0), 0)}
                              </td>
                            </tr>
                          );
                        }
                        const unidad = entry.unidad;
                        const perteneceAGrupo = !!entry.grupoId;
                        return (
                        <tr key={unidad.unidad_id} className={cn(
                          "hover:bg-slate-100/50",
                          perteneceAGrupo && "bg-indigo-50/40"
                        )}>
                          <td className={cn(
                            "py-2 px-3 font-medium",
                            perteneceAGrupo ? "text-indigo-800 border-l-4 border-l-indigo-400" : "text-slate-900"
                          )}>
                            {perteneceAGrupo && <span className="inline-block w-4"></span>}
                            <span>{unidad.nombre_unidad}</span>
                          </td>
                          {tiposEgreso.map(tipo => (
                            <td key={tipo.key} className="py-1 px-2">
                              {tipo.auto ? (
                                <div className="h-8 flex items-center justify-end px-2 text-sm font-mono bg-slate-100 rounded border border-slate-200 text-slate-600">
                                  $ {formatImporte(getEgresoValue(unidad.unidad_id, tipo.key), 0)}
                                </div>
                              ) : tipo.key === 'expensas' ? (
                                <div data-testid={`expensas-input-${unidad.unidad_id}`} className={cn(
                                  'rounded',
                                  egresosData[`${unidad.unidad_id}-${selectedPeriodo}`]?.expensas_manual && 'ring-1 ring-amber-300'
                                )}>
                                  <EgresoInput
                                    value={getEgresoValue(unidad.unidad_id, 'expensas')}
                                    onChange={(val) => handleEgresoChange(unidad.unidad_id, 'expensas', val)}
                                    formatImporte={formatImporte}
                                  />
                                </div>
                              ) : (
                                <EgresoInput
                                  value={getEgresoValue(unidad.unidad_id, tipo.key)}
                                  facturas={egresosData[`${unidad.unidad_id}-${selectedPeriodo}`]?.[`${tipo.key}_facturas`]}
                                  onChange={(val) => handleEgresoChange(unidad.unidad_id, tipo.key, val)}
                                  formatImporte={formatImporte}
                                />
                              )}
                            </td>
                          ))}
                          <td className="py-2 px-3 text-right font-mono text-sm font-semibold whitespace-nowrap">
                            <span className="text-green-700">
                              $ {formatImporte(calcularTotalUnidad(unidad.unidad_id), 0)}
                            </span>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                      <tr className="font-semibold">
                        <td className="py-3 px-3"></td>
                        {tiposEgreso.map(tipo => (
                          <td key={tipo.key} className="py-3 px-3 text-right font-mono text-slate-900 whitespace-nowrap">
                            $ {formatImporte(calcularTotalColumna(tipo.key), 0)}
                          </td>
                        ))}
                        <td className="py-3 px-3 text-right font-mono text-green-700 whitespace-nowrap">
                          $ {formatImporte(calcularTotalGeneral(), 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            );
          })()}
        </TabsContent>

        <AlertDialog open={syncConfirmOpen} onOpenChange={setSyncConfirmOpen}>
          <AlertDialogContent data-testid="sync-expensas-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Actualizar expensas desde la liquidación</AlertDialogTitle>
              <AlertDialogDescription>
                Hay valores de expensas cargados manualmente en este período. Si continuás,
                se reemplazarán por los montos de la liquidación aprobada. Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="sync-expensas-cancel" disabled={syncing}>Cancelar</AlertDialogCancel>
              <AlertDialogAction data-testid="sync-expensas-confirm-btn" onClick={(e) => { e.preventDefault(); doSyncExpensas(); }} disabled={syncing}>
                {syncing ? 'Actualizando…' : 'Pisar y actualizar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        </>
  );
};
